import React, { useEffect, useState } from "react";
import { 
  Container, Typography, Button, Box, Paper,
  Card, CardContent, CardHeader, Alert
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SecurityIcon from '@mui/icons-material/Security';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ConstructionIcon from '@mui/icons-material/Construction';
import { Application } from "@microsoft/microsoft-graph-types";

interface SamlTesterProps {
  app: Application;
  onBack: () => void;
  msalInstance?: any;
}

// Keep environment detection for future use
function detectEnvironment(): 'development' | 'staging' | 'production' {
  if (process.env.NODE_ENV === 'development') {
    return 'development';
  }
  
  const hostname = window.location.hostname;
  
  if (hostname.includes('staging') || 
      hostname.includes('-staging.') || 
      hostname.match(/.*-\w+\.westeurope\.6\.azurestaticapps\.net/) && hostname.includes('staging')) {
    return 'staging';
  }
  
  return 'production';
}

const SamlTester: React.FC<SamlTesterProps> = ({ app, onBack, msalInstance }) => {
  const [environmentInfo, setEnvironmentInfo] = useState<any>({});

  useEffect(() => {
    const envInfo = {
      environment: detectEnvironment(),
      hostname: window.location.hostname,
      origin: window.location.origin,
      nodeEnv: process.env.NODE_ENV,
      msalInstanceAvailable: !!msalInstance,
      windowMsalAvailable: !!window.msal
    };
    
    setEnvironmentInfo(envInfo);
    console.log("[SamlTester] Environment Info:", envInfo);
  }, [msalInstance]);

  return (
    <Container sx={{ mt: 4 }}>
      {/* Header */}
      <Box 
        sx={{ 
          display: "flex", 
          alignItems: "center", 
          mb: 3,
          borderLeft: '4px solid',
          borderColor: 'secondary.main',
          pl: 2,
          py: 1,
        }}
      >
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />} 
          onClick={onBack} 
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        
        <Box>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 600,
              color: 'secondary.main',
              fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              letterSpacing: '-0.02em',
              mb: 0.5
            }}
          >
            SAML Authentication Tester
          </Typography>
          
          <Typography 
            variant="subtitle1" 
            sx={{ 
              color: 'text.secondary',
              fontWeight: 400,
              fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
            }}
          >
            Testing SAML flows for <strong>{app.displayName}</strong> - Environment: {detectEnvironment()}
          </Typography>
        </Box>
      </Box>

      {/* Coming Soon Alert */}
      <Alert 
        severity="info" 
        icon={<ConstructionIcon />}
        sx={{ 
          mb: 3,
          borderRadius: 2,
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          SAML Testing Coming Soon
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Generic SAML SP-initiated flow testing is planned for a future release. 
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>Planned features:</strong> Dynamic certificate fetching, configurable issuer formats, 
          support for any SAML enterprise application, and comprehensive claim validation.
        </Typography>
      </Alert>

      {/* Application Information */}
      <Card sx={{ mb: 3 }}>
        <CardHeader 
          title="SAML Application Information" 
          avatar={<SecurityIcon color="secondary" />}
        />
        <CardContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Display Name:</strong> {app.displayName || 'Not available'}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>App ID:</strong> {app.appId || 'Not available'}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Publisher Domain:</strong> {app.publisherDomain || 'Not available'}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Sign-in Audience:</strong> {app.signInAudience || 'Not available'}
          </Typography>
          <Typography variant="body1">
            <strong>Authentication Type:</strong> SAML 2.0
          </Typography>
        </CardContent>
      </Card>

      {/* Current Workaround */}
      {/* <Card variant="outlined">
        <CardHeader
          title="Current Workaround for SAML Testing"
          titleTypographyProps={{ variant: "h6" }}
          avatar={<InfoOutlinedIcon color="primary" />}
          sx={{ backgroundColor: "#f5f7fa", borderBottom: "1px solid #e0e0e0" }}
        />
        <CardContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            To test SAML authentication for this application, you can:
          </Typography>
          
          <Box component="ol" sx={{ pl: 2, mb: 2 }}>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>IdP-Initiated Flow:</strong> Go to{' '}
              <a href="https://myapps.microsoft.com" target="_blank" rel="noopener noreferrer">
                My Apps (myapps.microsoft.com)
              </a>{' '}
              and click on "{app.displayName}"
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Direct SAML URL:</strong> If your application has a direct SAML login URL, 
              use that to initiate the authentication flow
            </Typography>
            <Typography component="li" variant="body2">
              <strong>Azure Portal Testing:</strong> Use the "Test" feature in Azure Portal → 
              Enterprise Applications → {app.displayName} → Single sign-on
            </Typography>
          </Box>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> The current SAML testing infrastructure is preserved and will be 
              enhanced to support generic SAML applications in a future update.
            </Typography>
          </Alert>
        </CardContent>
      </Card> */}

      {/* Environment Information (collapsed by default) */}
      <Card sx={{ mt: 3 }}>
        <CardHeader 
          title="Environment Information" 
          sx={{ 
            backgroundColor: '#f9f9f9',
            '& .MuiCardHeader-title': { fontSize: '0.9rem', color: 'text.secondary' }
          }}
        />
        <CardContent sx={{ backgroundColor: '#fafafa' }}>
          <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem', overflow: 'auto' }}>
            {JSON.stringify(environmentInfo, null, 2)}
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default SamlTester;